interface MergeInteractionFormStatesOptions<TTemplate, TState> {
  /** Construit l'état local d'un template du store, en reprenant l'état précédent s'il existe. */
  toState: (template: TTemplate, previousState: TState | undefined) => TState;
  /** Un brouillon n'a pas encore de template en base. */
  isDraft: (state: TState) => boolean;
  /**
   * Éléments dont la sauvegarde est en cours : un listing intermédiaire ne porte qu'un template
   * partiel, on garde l'état local jusqu'à la fin de l'enregistrement.
   */
  savingIds?: ReadonlySet<string>;
}

/**
 * Fusionne l'état local d'une liste d'interactions (quiz, sondages) avec la liste du store.
 * Un élément enregistré porte l'id de son template en chaîne ; un brouillon porte un id local.
 * Reconstruire la liste depuis le store seul démonterait les brouillons et le formulaire de
 * l'élément en cours de sauvegarde.
 */
export const mergeInteractionFormStates = <
  TTemplate extends { id: number },
  TState extends { id: string },
>(
  previousStates: TState[],
  templates: TTemplate[],
  { toState, isDraft, savingIds }: MergeInteractionFormStatesOptions<TTemplate, TState>,
): TState[] => {
  const savedStates = templates.map((template) => {
    const previousState = previousStates.find((state) => state.id === String(template.id));
    const isSaving = previousState != null && savingIds?.has(previousState.id) === true;
    return isSaving ? previousState : toState(template, previousState);
  });

  const renderedIds = new Set(savedStates.map((state) => state.id));

  const keptStates = previousStates.filter(
    (state) => !renderedIds.has(state.id) && (isDraft(state) || savingIds?.has(state.id) === true),
  );

  return [...savedStates, ...keptStates];
};
